// Adapted from apps/admin/src/pages/dashboard/gps-tracking/ui/gps-tracking.page.tsx (PRD #875): the
// period, the truck and the playback moment live in the URL, trucks group by organization instead
// of by driver, and the list takes the govdash district / MVO filter.
import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { useMediaQuery } from "@racoon/ui";
import {
  findPositionAtTime,
  gpsPointsToTimelinePoints,
  createRouteTimelineSegments,
} from "@racoon/ui";
import type { ConnectProto } from "@racoon/api";
import { useT, Namespace, type TranslationKey } from "@racoon/i18n";
import { photoPointsForJobs } from "~/features/map-view/model/helpers/photo-points";
import { PhotoReportOpenerContext } from "~/features/map-view/components/PhotoPointsLayer";
import { useGpsStops } from "~/features/gps-stops";
import type { ExtraFilterItem } from "~/features/filter/filter.form";
import { FilterForm } from "~/features/filter/filter.form";
import { useGetGovGeoAccessQuery } from "~/shared/api/govDash/govDash.api";
import { useCanEditGovDash } from "~/shared/hooks/useCanEditGovDash";
import { useDashboardFilters } from "~/pages/dashboard/lib/use-dashboard-filters.hook";
import { VehicleList } from "../components/vehicle-list";
import { MapViewSection } from "../components/map-view-section";
import { GpsPhotoReportModal } from "../components/photo-report.modal";
import { jobForReport } from "../model/photo-evidence";
import { useGpsTrackingData } from "../model/useGpsTrackingData";
import { GpsTrackingPoint, ViewMode, TimelineSource } from "../model/types";
import {
  dayPeriod,
  gpsTrackingSearch,
  parseGpsTrackingParams,
  type GpsTrackingUrlState,
} from "../model/url-state";

// The segment builder passes its GpsTracking keys as plain strings.
type GpsTrackingKey = TranslationKey<Namespace.GpsTracking>;

/** The playback moment reaches the URL at most this often while the player runs. */
const TIME_URL_THROTTLE_MS = 1000;

/** The trucks filter row belongs to the map; this screen lists MVO trucks only. */
const LIST_FILTER_KEYS = ["district", "mvo"];

export function GpsTrackingPage() {
  const t = useT();
  const isDesktop = useMediaQuery("(min-width: 1024px)");
  const canEditGovDash = useCanEditGovDash();

  // Editors only: an auditor following a shared link lands on the map.
  if (!canEditGovDash) return <Navigate to="/dashboard" replace />;

  if (!isDesktop) {
    return (
      <div
        data-testid="gps-tracking-desktop-only"
        className="flex h-full w-full items-center justify-center p-6 text-center text-sm text-text-secondary"
      >
        {t(Namespace.Govdash, "gpsTracking.desktopOnly")}
      </div>
    );
  }

  return <GpsTrackingScreen />;
}

function GpsTrackingScreen() {
  const t = useT();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState("");
  const [viewMode, setViewMode] = useState<ViewMode>("map");
  const [selectedPointId, setSelectedPointId] = useState<string>();
  const [playbackTime, setPlaybackTime] = useState<number | null>(null);
  const [isPlaybackActive, setIsPlaybackActive] = useState(false);
  const [timelineSource, setTimelineSource] = useState<TimelineSource>("all");
  const [initialPlaybackTime, setInitialPlaybackTime] = useState<number | null>(null);
  const [filterOpen, setFilterOpen] = useState(false);
  const [reportJob, setReportJob] = useState<ConnectProto.Job>();

  // "Now" is read whenever the URL changes, so a link without a day opens today.
  const { state: urlState, corrected } = useMemo(
    () => parseGpsTrackingParams(searchParams, new Date()),
    [searchParams],
  );
  const { day, truckId: selectedVehicleId } = urlState;
  const dayTime = day.getTime();
  const { start, end } = useMemo(() => dayPeriod(new Date(dayTime)), [dayTime]);

  // A hand-edited or stale link is shown corrected, and its URL says so.
  useEffect(() => {
    if (corrected) setSearchParams(gpsTrackingSearch(urlState), { replace: true });
  }, [corrected, urlState, setSearchParams]);

  const navigateTo = useCallback(
    (next: Partial<GpsTrackingUrlState>) => {
      setSearchParams(gpsTrackingSearch({ ...urlState, ...next }));
    },
    [urlState, setSearchParams],
  );

  const { data: govGeoAccessResp } = useGetGovGeoAccessQuery({});
  const govGeoAccesses = useMemo(() => govGeoAccessResp?.govGeoAccesses ?? [], [govGeoAccessResp]);
  const { extraFilters, setExtraFilters, govOrganizationQueryArg } =
    useDashboardFilters(govGeoAccesses);
  const listFilters = useMemo(
    () => extraFilters.filter((filter) => LIST_FILTER_KEYS.includes(filter.key)),
    [extraFilters],
  );
  const activeFilterCount = listFilters.reduce(
    (count, filter) => count + filter.selectedOptions.length,
    0,
  );
  const districtOrganizationIds = useMemo(
    () => govOrganizationQueryArg.districtOrganizationIds.map((id) => BigInt(id)),
    [govOrganizationQueryArg],
  );
  const serviceProviderIds = useMemo(
    () => govOrganizationQueryArg.serviceProviderIds.map((id) => BigInt(id)),
    [govOrganizationQueryArg],
  );

  const handleFilterApply = useCallback(
    ({ newSelected }: { newSelected: ExtraFilterItem[] }) => {
      setExtraFilters((prev) =>
        prev.map((filter) => newSelected.find((next) => next.key === filter.key) ?? filter),
      );
      setFilterOpen(false);
    },
    [setExtraFilters],
  );

  const {
    vehicles,
    hasRoutes,
    routesLoading,
    routesError,
    refetchRoutes,
    gpsPoints,
    trackLoading,
    trackError,
    trackNotFound,
    selectedVehicle,
    offListTruck,
    usedPickupLocations,
    stepPins,
    usedDepots,
    routes,
  } = useGpsTrackingData({
    start,
    end,
    districtOrganizationIds,
    serviceProviderIds,
    searchQuery,
    selectedVehicleId,
  });

  const handlePointClick = useCallback(
    (point: GpsTrackingPoint) => {
      setSelectedPointId(point.id);
      if (selectedVehicleId !== point.vehicleId) {
        navigateTo({ truckId: point.vehicleId, time: undefined });
      }
    },
    [selectedVehicleId, navigateTo],
  );

  const handleVehicleSelect = useCallback(
    (vehicleId: string) => {
      const isTogglingOff = selectedVehicleId === vehicleId;
      navigateTo({ truckId: isTogglingOff ? undefined : vehicleId, time: undefined });
    },
    [selectedVehicleId, navigateTo],
  );

  const handleDayChange = useCallback(
    (nextDay: Date) => navigateTo({ day: nextDay, time: undefined }),
    [navigateTo],
  );

  // The time span of the whole track, deliberately independent of playback.
  //
  // `displayGpsPoints` below is truncated at `playbackTime`, so deriving a
  // fetch window from it would move the window on every playback tick. Anything
  // keyed on that window (the GPS-stops query) would then refetch per tick, and
  // with an RTK Query cache key that changes each time, each tick becomes its
  // own cache entry rather than a hit. Fetch once for the full span and let the
  // consumer reveal things progressively.
  const trackWindow = useMemo(() => {
    if (gpsPoints.length === 0) return { from: null, to: null };

    let min = gpsPoints[0].timestamp.getTime();
    let max = min;
    for (const point of gpsPoints) {
      const time = point.timestamp.getTime();
      if (time < min) min = time;
      if (time > max) max = time;
    }

    return { from: new Date(min), to: new Date(max) };
  }, [gpsPoints]);

  // The photo report's evidence reads the same stops the map draws; with the same arguments
  // the request is the layer's own, from the cache.
  const { stops: reportStops } = useGpsStops({
    truckId: selectedVehicle?.id,
    from: trackWindow.from,
    to: trackWindow.to,
    includeUnmatched: true,
    enabled: Boolean(reportJob),
  });

  const openReport = useCallback(
    (job: ConnectProto.Job) => setReportJob(jobForReport(job, routes)),
    [routes],
  );

  const photoPoints = useMemo(
    () => photoPointsForJobs(routes.flatMap((route) => route.jobs ?? [])),
    [routes],
  );

  // Timeline player data
  const timelinePoints = useMemo(() => {
    return gpsPointsToTimelinePoints(gpsPoints, routes);
  }, [gpsPoints, routes]);

  const pendingTimeRef = useRef<number | null>(null);
  const timeWriteTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cancelTimeWrite = useCallback(() => {
    if (timeWriteTimerRef.current) clearTimeout(timeWriteTimerRef.current);
    timeWriteTimerRef.current = null;
    pendingTimeRef.current = null;
  }, []);
  useEffect(() => cancelTimeWrite, [cancelTimeWrite]);

  // The player follows the URL's playback moment when a new truck or period opens and when
  // the moment changed from outside: a link, Back / Forward. Moments this screen wrote itself
  // are skipped, or every write would seek the player and stop it.
  const playbackKey = `${selectedVehicleId}|${dayTime}`;
  const playbackKeyRef = useRef<string | null>(null);
  const writtenTimeRef = useRef<number | undefined>(undefined);
  useEffect(() => {
    const keyChanged = playbackKeyRef.current !== playbackKey;
    playbackKeyRef.current = playbackKey;
    if (!keyChanged && urlState.time === writtenTimeRef.current) return;

    if (keyChanged) {
      cancelTimeWrite();
      // The report belongs to the previous truck or period.
      setReportJob(undefined);
    }
    writtenTimeRef.current = urlState.time;
    const time = urlState.time ?? null;
    setInitialPlaybackTime(time);
    setPlaybackTime(time);
    // Active, so the truck marker stands at the linked moment before anything plays.
    setIsPlaybackActive(time !== null);
  }, [urlState.time, playbackKey, cancelTimeWrite]);

  const writeTimeToUrl = useCallback(
    (time: number) => {
      pendingTimeRef.current = time;
      if (timeWriteTimerRef.current) return;
      const truckId = selectedVehicleId;
      timeWriteTimerRef.current = setTimeout(() => {
        timeWriteTimerRef.current = null;
        const pending = pendingTimeRef.current;
        if (pending === null) return;
        const rounded = Math.round(pending);
        // Replaced, not pushed: Back steps over the playback to the previous truck or period.
        setSearchParams(
          (prev) => {
            // Another truck opened before the write ran: the moment was the old truck's.
            if (prev.get("truckId") !== truckId) return prev;
            writtenTimeRef.current = rounded;
            const next = new URLSearchParams(prev);
            next.set("time", String(rounded));
            return next;
          },
          { replace: true },
        );
      }, TIME_URL_THROTTLE_MS);
    },
    [selectedVehicleId, setSearchParams],
  );

  // Fact-based segments drive the map playback timeline; the table can also
  // show the GPS-derived ones, depending on the selected source.
  const factSegments = useMemo(
    () =>
      createRouteTimelineSegments(
        routes,
        timelinePoints,
        (key, options) => t(Namespace.GpsTracking, key as GpsTrackingKey, options),
        "fact",
      ),
    [routes, timelinePoints, t],
  );

  const gpsSegments = useMemo(
    () =>
      createRouteTimelineSegments(
        routes,
        timelinePoints,
        (key, options) => t(Namespace.GpsTracking, key as GpsTrackingKey, options),
        "gps",
      ),
    [routes, timelinePoints, t],
  );

  const tableSegments = useMemo(() => {
    if (timelineSource === "fact") return factSegments;
    if (timelineSource === "gps") return gpsSegments;
    return [...factSegments, ...gpsSegments].sort((a, b) => a.startTime - b.startTime);
  }, [timelineSource, factSegments, gpsSegments]);

  // When playback is active, filter GPS points to show only up to current time
  const displayGpsPoints = useMemo(() => {
    if (!isPlaybackActive || playbackTime === null) {
      return gpsPoints;
    }

    // Show points up to current playback time
    return gpsPoints.filter((point: GpsTrackingPoint) => point.timestamp.getTime() <= playbackTime);
  }, [gpsPoints, playbackTime, isPlaybackActive]);

  // Calculate current vehicle position during playback
  const playbackPosition = useMemo(() => {
    if (!isPlaybackActive || timelinePoints.length === 0 || playbackTime === null) {
      return null;
    }

    return findPositionAtTime(timelinePoints, playbackTime);
  }, [playbackTime, timelinePoints, isPlaybackActive]);

  const handleTimeChange = useCallback(
    (newTime: number) => {
      setPlaybackTime(newTime);
      setIsPlaybackActive(true);
      writeTimeToUrl(newTime);
    },
    [writeTimeToUrl],
  );

  return (
    <div className="flex h-full w-full overflow-hidden gap-4">
      <div className="w-1/3 min-w-[320px] max-w-[400px] flex flex-col h-full rounded-2xl">
        <VehicleList
          vehicles={vehicles}
          hasRoutes={hasRoutes}
          loading={routesLoading}
          error={routesError}
          onRetry={refetchRoutes}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          day={day}
          onDayChange={handleDayChange}
          activeFilterCount={activeFilterCount}
          onOpenFilters={() => setFilterOpen(true)}
          selectedVehicleId={selectedVehicleId}
          onVehicleSelect={handleVehicleSelect}
          offListTruck={offListTruck}
        />
      </div>

      <div className="flex-1 flex flex-col h-full min-w-0 overflow-hidden gap-4">
        <div className="flex-1 min-h-0">
          <PhotoReportOpenerContext.Provider value={openReport}>
            <MapViewSection
              viewMode={viewMode}
              onViewModeChange={setViewMode}
              gpsPoints={displayGpsPoints}
              trackWindow={trackWindow}
              selectedVehicle={selectedVehicle}
              selectedPointId={selectedPointId}
              onPointClick={handlePointClick}
              loading={trackLoading}
              trackError={trackError}
              trackNotFound={trackNotFound}
              pickupLocations={usedPickupLocations}
              stepPins={stepPins}
              depots={usedDepots}
              playbackPosition={playbackPosition}
              playbackTime={playbackTime}
              timelinePoints={timelinePoints}
              timelineSegments={factSegments}
              tableSegments={tableSegments}
              onTimeChange={handleTimeChange}
              isPlaybackActive={isPlaybackActive}
              hasVehicles={vehicles.length > 0}
              timelineSource={timelineSource}
              onTimelineSourceChange={setTimelineSource}
              initialPlaybackTime={initialPlaybackTime}
              photoPoints={photoPoints}
            />
          </PhotoReportOpenerContext.Provider>
        </div>
      </div>

      <GpsPhotoReportModal
        job={reportJob}
        track={gpsPoints}
        stops={reportStops}
        onClose={() => setReportJob(undefined)}
      />

      <FilterForm
        open={filterOpen}
        onOpenChange={setFilterOpen}
        onApply={handleFilterApply}
        showFilterTabs={false}
        extraFilters={listFilters}
      />
    </div>
  );
}
